import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';

interface HealthData {
  status: string;
  timestamp: string;
  services: {
    database: { status: string; message: string };
    redis: { status: string; message: string };
    stripe: { status: string; message: string };
    azure: { status: string; message: string };
  };
}

interface SystemStats {
  tenants: number;
  users: number;
  templates: number;
  admins: number;
  databaseSize: string;
}

export default function System({ adminUser }: any) {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    fetchData();
    // Refresh health every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const [healthRes, statsRes] = await Promise.all([
        fetch(`${apiUrl}/api/system/health`, { credentials: 'include' }),
        fetch(`${apiUrl}/api/system/stats`, { credentials: 'include' }),
      ]);

      if (healthRes.ok) {
        const data = await healthRes.json();
        setHealth(data.data);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching system data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = async () => {
    setClearing(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/system/cache/clear`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Cache cleared successfully');
      } else {
        toast.error('Failed to clear cache');
      }
    } catch (error) {
      toast.error('Error clearing cache');
    } finally {
      setClearing(false);
    }
  };

  const cardStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  const getStatusColor = (status: string) => {
    if (status === 'healthy') return '#10b981';
    if (status === 'degraded') return '#f59e0b';
    return '#ef4444';
  };

  const getStatusIcon = (status: string) => {
    if (status === 'healthy') return '✓';
    if (status === 'degraded') return '⚠';
    return '✗';
  };

  return (
    <>
      <Navigation adminUser={adminUser} />
      <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto', background: '#f9fafb', minHeight: 'calc(100vh - 64px)' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '32px' }}>
          System Health
        </h1>

        {loading ? (
          <div style={{ color: '#666' }}>Loading system health...</div>
        ) : (
          <>
            {/* Overall Health Status */}
            {health && (
              <div style={{ ...cardStyle, marginBottom: '32px', background: getStatusColor(health.status), color: 'white' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                      System Status: {health.status.toUpperCase()}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>
                      Last checked: {new Date(health.timestamp).toLocaleString('da-DK')}
                    </div>
                  </div>
                  <div style={{ fontSize: '48px' }}>
                    {getStatusIcon(health.status)}
                  </div>
                </div>
              </div>
            )}

            {/* Service Status Grid */}
            {health && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                {Object.entries(health.services).map(([service, data]) => (
                  <div key={service} style={cardStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontSize: '24px', marginRight: '12px', color: getStatusColor(data.status) }}>
                        {getStatusIcon(data.status)}
                      </span>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827', textTransform: 'capitalize' }}>
                        {service}
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      {data.message}
                    </div>
                    <div style={{ fontSize: '12px', color: getStatusColor(data.status), marginTop: '8px', fontWeight: '500' }}>
                      {data.status.toUpperCase()}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* System Statistics */}
            {stats && (
              <div style={cardStyle}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>
                  System Statistics
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Tenants</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{stats.tenants}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Users</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{stats.users}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Templates</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{stats.templates}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Admin Users</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{stats.admins}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Database Size</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{stats.databaseSize}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Maintenance Actions */}
            <div style={{ ...cardStyle, marginTop: '32px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>
                Maintenance Actions
              </h2>
              <button
                onClick={handleClearCache}
                disabled={clearing}
                style={{
                  padding: '12px 24px',
                  background: clearing ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: clearing ? 'not-allowed' : 'pointer',
                }}
              >
                {clearing ? 'Clearing...' : 'Clear Cache'}
              </button>
              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '12px' }}>
                Clear application cache (Redis). Use this if you're experiencing stale data issues.
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
